import { useMemo, useState } from "react";
import { Box, Checkbox, Divider, Group, ScrollArea, Stack, Switch, Text, TextInput } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { FaSearch } from "react-icons/fa";
import { getInstanceClassMeta, IFC_INSTANCE_CLASS_META, VENTILATION_IFC_CLASSES } from "@multiconsult-group/multiplacer-core";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { setIfcClassOpacityInRedux, setIfcClassVisibilityInRedux } from "../../designSlice";
import { selectAllIfcClassesToToggle, selectInstanceCountsByClass } from "../../../McBim/mcbimItemSelectors";
import classes from "./IfcClassesMenuContent.module.css";

const disciplines = ["ARK", "Ventilasjon", "Rør og øvrig VVS", "RIE", "Øvrig"] as const;
const ventilationClasses = new Set<string>(VENTILATION_IFC_CLASSES);
const getClassGroup = (ifcClass: string): typeof disciplines[number] => {
  if (ventilationClasses.has(ifcClass)) return "Ventilasjon";
  const discipline = IFC_INSTANCE_CLASS_META[ifcClass]?.discipline;
  return discipline === "RIV" ? "Rør og øvrig VVS" : discipline ?? "Øvrig";
};
const normalize = (value: string) => value.trim().toLocaleLowerCase("nb-NO");

export const IfcClassesMenuContent = () => {
  const dispatch = useAppDispatch();
  const all = useAppSelector(selectAllIfcClassesToToggle);
  const counts = useAppSelector(selectInstanceCountsByClass);
  const selected = useAppSelector(state => state.design.ifcClassesToDisplay);
  const [search, setSearch] = useState("");
  const [grouped, setGrouped] = useLocalStorage({ key: "ifc-class-list-grouped", defaultValue: false });
  const selectedByClass = useMemo(() => new Map(selected.map(entry => [entry.ifcClass, entry])), [selected]);

  const filtered = useMemo(() => {
    const query = normalize(search);
    return all.filter(ifcClass => {
      const meta = getInstanceClassMeta(ifcClass);
      return normalize(`${meta.label ?? ""} ${ifcClass}`).includes(query);
    }).sort((a, b) => (getInstanceClassMeta(a).label ?? a).localeCompare(getInstanceClassMeta(b).label ?? b, "nb-NO"));
  }, [all, search]);
  const sections = grouped
    ? disciplines.map(name => ({ name, items: filtered.filter(ifcClass => getClassGroup(ifcClass) === name) }))
    : [{ name: "", items: filtered }];
  const selectedCount = filtered.filter(ifcClass => selectedByClass.has(ifcClass)).length;
  const allSelected = filtered.length > 0 && selectedCount === filtered.length;

  const setClassesVisible = (items: string[], visible: boolean) => {
    items.forEach(ifcClass => {
      if (selectedByClass.has(ifcClass) !== visible) {
        dispatch(setIfcClassVisibilityInRedux({ ifcClass, visible }));
      }
    });
  };

  return (
    <Stack gap="sm" h="100%" style={{ minHeight: 0 }}>
      <TextInput size="sm" placeholder="Søk etter klasse eller navn" aria-label="Søk i IFC-klasser"
        leftSection={<FaSearch size={13} />} value={search} onChange={event => setSearch(event.currentTarget.value)} />
      <Switch size="xs" label="Grupper etter disiplin" checked={grouped} onChange={event => setGrouped(event.currentTarget.checked)} />
      <Group justify="space-between" gap="xs">
        <Checkbox size="xs" label={search.trim() ? "Vis alle treff" : "Vis alle"} checked={allSelected}
          indeterminate={selectedCount > 0 && !allSelected} disabled={filtered.length === 0}
          onChange={() => setClassesVisible(filtered, !allSelected)} />
        <Text size="xs" c="dimmed">{selectedCount} / {filtered.length} valgt</Text>
      </Group>
      <Group justify="space-between">
        <Text size="xs" c="dimmed">IFC-klasse · antall</Text>
        <Text size="xs" c="dimmed">Opasitet · 2D</Text>
      </Group>
      <Divider />
      <ScrollArea type="auto" offsetScrollbars style={{ flex: 1, minHeight: 0 }}>
        <Box className={classes.list}>
          {sections.filter(section => section.items.length > 0).map(section => {
            const groupSelectedCount = section.items.filter(ifcClass => selectedByClass.has(ifcClass)).length;
            const groupAllSelected = groupSelectedCount === section.items.length;
            return (
            <section key={section.name} aria-label={section.name || "Alle IFC-klasser"}>
              {section.name && (
                <Group justify="space-between" mt="md" mb="xs" gap="xs">
                  <Checkbox size="xs" checked={groupAllSelected}
                    indeterminate={groupSelectedCount > 0 && !groupAllSelected}
                    aria-label={search.trim() ? `Vis alle treff i ${section.name}` : `Vis alle i ${section.name}`}
                    label={<Text component="h3" size="xs" fw={700} m={0}>{section.name}</Text>}
                    onChange={() => setClassesVisible(section.items, !groupAllSelected)} />
                  <Text size="xs" c="dimmed">{groupSelectedCount} / {section.items.length}</Text>
                </Group>
              )}
              {section.items.map(ifcClass => {
                const meta = getInstanceClassMeta(ifcClass);
                const label = meta.label ?? ifcClass;
                const entry = selectedByClass.get(ifcClass);
                const percent = Math.round((entry?.opacity ?? 1) * 100);
                return (
                  <div key={ifcClass} className={classes.row}>
                    <Checkbox checked={!!entry} aria-label={`Vis ${label} (${ifcClass})`}
                      onChange={event => dispatch(setIfcClassVisibilityInRedux({ ifcClass, visible: event.currentTarget.checked }))}
                      styles={{ body: { alignItems: "center" }, labelWrapper: { minWidth: 0 }, label: { paddingLeft: 8 } }}
                      label={<Group gap={8} wrap="nowrap">
                        <Box w={9} h={9} bg={meta.color ?? "gray"} style={{ borderRadius: 2, flexShrink: 0 }} />
                        <Box style={{ minWidth: 0 }}>
                          <Text size="sm" fw={entry ? 500 : 400} style={{ overflowWrap: "anywhere" }}>{label}</Text>
                          <Text size="xs" c="dimmed" style={{ overflowWrap: "anywhere" }}>{ifcClass} · {(counts[ifcClass] ?? 0).toLocaleString("nb-NO")}</Text>
                        </Box>
                      </Group>} />
                    <div className={classes.opacity}>
                      <input type="range" min={0} max={100} step={1} value={percent} disabled={!entry}
                        aria-label={`Opasitet for ${label} (${ifcClass})`} aria-valuetext={`${percent} %`}
                        onChange={event => dispatch(setIfcClassOpacityInRedux({ ifcClass, opacity: Number(event.currentTarget.value) / 100 }))} />
                      <Text size="xs" c="dimmed" ta="right" style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{entry ? `${percent} %` : "—"}</Text>
                    </div>
                  </div>
                );
              })}
            </section>
            );
          })}
          {filtered.length === 0 && <Text size="sm" c="dimmed" py="lg" ta="center">Ingen IFC-klasser funnet.</Text>}
        </Box>
      </ScrollArea>
    </Stack>
  );
};
